import type {
  ICreateWatchCompaniesOutput,
  ILookupPublicCompanyFacts,
  ILookupPublicCompanyInput,
  ILookupPublicCompanyOutput,
  ISearchWatchCompaniesOutput,
} from 'components/AiChat/interfaces/IAiChatTools';
import type IAiChatMessagePart from 'store/slices/aiChats/interfaces/IAiChatMessagePart';

/** User turn that asks the model to call createWatchCompanies (L33). */
export const CREATE_WATCH_COMPANIES_USER_TURN = [
  'Create the pending firms.',
  'Call createWatchCompanies.',
  'Do not put seed JSON in the chat text.',
].join(' ');

export interface IWatchCreatePendingRow {
  name: string,
  country: string,
  product: string,
  uid: string,
  skipUid: string | null,
}

function toolNameOf(part: IAiChatMessagePart): string {
  if (part.toolName) {
    return part.toolName;
  }
  if (part.type.startsWith('tool-')) {
    return part.type.slice(5);
  }
  return '';
}

function latestOutput<T>(
  parts: IAiChatMessagePart[],
  name: string,
): T | null {
  const matches = parts.filter((part) => (
    toolNameOf(part) === name && part.state === 'output-available' && part.output != null
  ));
  const last = matches[matches.length - 1];
  return last ? last.output as T : null;
}

export function flattenAiChatParts(
  messages: Array<{ parts?: IAiChatMessagePart[] }>,
): IAiChatMessagePart[] {
  return [...messages].reverse().flatMap((message) => message.parts || []);
}

export function lastCreateWatchCompaniesOutput(
  messages: Array<{ parts?: IAiChatMessagePart[] }>,
): ICreateWatchCompaniesOutput | null {
  return latestOutput<ICreateWatchCompaniesOutput>(
    flattenAiChatParts(messages),
    'createWatchCompanies',
  );
}

function lookupInputOf(part: IAiChatMessagePart): ILookupPublicCompanyInput | undefined {
  return part.input as ILookupPublicCompanyInput | undefined;
}

function namedFirmsFromLookup(output: ILookupPublicCompanyOutput): ILookupPublicCompanyFacts[] {
  if (output.error) {
    return [];
  }
  const firms = output.companies?.length ? output.companies : [output.facts];
  return firms.filter((facts): facts is ILookupPublicCompanyFacts => (
    Boolean(facts?.name?.trim())
  ));
}

function failedLookupLabels(lookupParts: IAiChatMessagePart[]): Set<string> {
  const labels = new Set<string>();
  lookupParts.forEach((part) => {
    const output = part.output as ILookupPublicCompanyOutput;
    if (namedFirmsFromLookup(output).length) {
      return;
    }
    const input = lookupInputOf(part);
    [input?.query, input?.name].forEach((value) => {
      const label = value?.trim().toLowerCase();
      if (label) {
        labels.add(label);
      }
    });
  });
  return labels;
}

export function pendingRowsFromAiChatMessages(
  messages: Array<{ parts?: IAiChatMessagePart[] }>,
): IWatchCreatePendingRow[] {
  const parts = flattenAiChatParts(messages);
  const search = latestOutput<ISearchWatchCompaniesOutput>(parts, 'searchWatchCompanies');
  const lookupParts = parts.filter((part) => (
    toolNameOf(part) === 'lookupPublicCompany' && part.state === 'output-available'
  ));

  const lookupRows = lookupParts.flatMap((part) => {
    const output = part.output as ILookupPublicCompanyOutput;
    const input = lookupInputOf(part);
    return namedFirmsFromLookup(output).map((facts) => {
      const name = (facts.name || '').trim();
      const hitGroup = search?.hits.find((item) => item.seedName === name);
      const skip = hitGroup?.hits[0];
      return {
        name,
        country: facts.addressCountry || input?.addressCountry || '—',
        product: facts.productName || input?.productName || '—',
        uid: skip?.uid || '',
        skipUid: skip?.uid || null,
      };
    });
  });

  if (lookupRows.length) {
    return lookupRows;
  }

  if (!search?.hits.length) {
    return [];
  }

  const skipLabels = failedLookupLabels(lookupParts);
  return search.hits.flatMap((group) => {
    const name = group.seedName?.trim() || '';
    if (!name || skipLabels.has(name.toLowerCase())) {
      return [];
    }
    const skip = group.hits[0];
    return [{
      name,
      country: '—',
      product: '—',
      uid: skip?.uid || group.seedUid || '',
      skipUid: skip?.uid || null,
    }];
  });
}

export function createWatchCompaniesCommitKind(
  output: ICreateWatchCompaniesOutput | null,
): 'none' | 'invalid' | 'all_skipped' | 'created' {
  if (!output) {
    return 'none';
  }
  if (output.error) {
    return 'invalid';
  }
  if ((output.created?.length || 0) > 0) {
    return 'created';
  }
  return 'all_skipped';
}
